import { isEmpty } from '@ember/utils'
import type { NormalizedInterval } from 'date-fns'
import type { SchoolScope, StudentsScope } from 'teacher-ui-client/reporting/ui-scope/utils'
import type StudentEventsService from 'teacher-ui-client/services/student-events'

export interface MentalMinuteOverviewResponse {
  mental_minute_overview: {
    aggregate: MentalMinuteOverviewAggregate
    summary: {
      division: SummaryData
      multiplication: SummaryData
      addition: SummaryData
      subtraction: SummaryData
    }
    students: {
      student_id: number
      correct_answers: number
      time_on_task: number
      sprints_mastered: number
      sprints_attempted: number
      highest_skill_mastered: number
      scores: {
        average_improvement: number
        average_best_score: number
        average_first_score: number
      }
    }[]
  }
}

export interface MentalMinuteOverviewAggregate {
  time_on_task: {
    average: number
    total: number
  }
  sprints_mastered: {
    average: number
    total: number
  }
  sprints_attempted: {
    average: number
    total: number
  }
  correct_answers: {
    average: number
    total: number
  }
  scores: {
    average_first_score: number
    average_best_score: number
    average_improvement: number
  }
}

interface SummaryData {
  correct_answers: number
  improvement: number
  sprints_mastered: number
}

export interface MentalMinuteOverviewData {
  aggregate: MentalMinuteOverviewAggregate
  summary: {
    division: SummaryData
    multiplication: SummaryData
    addition: SummaryData
    subtraction: SummaryData
  }
  students: {
    student_id: number
    correct_answers: number
    time_on_task: number
    sprints_mastered: number
    sprints_attempted: number
    highest_skill_mastered: number
    scores: {
      average_improvement: number
      average_best_score: number
      average_first_score: number
    }
  }[]
}

export async function mentalMinuteOverview(
  this: StudentEventsService,
  product: LegacyProductMs,
  statsScope: SchoolScope | StudentsScope,
  period: NormalizedInterval,
): Promise<Nullable<MentalMinuteOverviewData>> {
  if (isEmpty(statsScope.ids)) return null

  const url = this.buildUrl('mental_minute_overview', product, statsScope)
  const query = this.buildQuery(statsScope, period)

  const data = await this.fetchData<MentalMinuteOverviewResponse>(url, query)
  if (!data) return null

  const {
    mental_minute_overview: { summary, aggregate, students },
  } = data
  if (isEmpty(students)) return null

  return { summary, aggregate, students }
}
